/**
 * Luxury Product JavaScript
 * Handles gallery interactions, variant hover preview, and dynamic updates
 */

class LuxuryProductGallery {
  constructor(container) {
    this.container = container;
    this.thumbnails = container.querySelectorAll('.luxury-product__thumbnail');
    this.images = container.querySelectorAll('.luxury-product__image');
    this.currentIndex = 0;

    this.init();
  }

  init() {
    this.thumbnails.forEach((thumb, index) => {
      thumb.addEventListener('click', () => {
        if (thumb.dataset.mediaId) {
          this.switchToMediaId(thumb.dataset.mediaId);
        } else {
          this.switchImage(index);
        }
      });
    });
  }

  switchImage(index) {
    const targetImage = this.images[index];
    if (!targetImage) return;

    this.restoreMediaImage(targetImage);
    this.activateImage(targetImage);
    this.currentIndex = index;
  }

  switchToMediaId(mediaId) {
    if (!mediaId) return;

    const targetImage = Array.from(this.images).find(
      image => String(image.dataset.mediaId) === String(mediaId)
    );

    if (targetImage) {
      const index = Array.from(this.images).indexOf(targetImage);
      this.restoreMediaImage(targetImage);
      this.activateImage(targetImage);
      this.currentIndex = index;
    }
  }

  switchToVariantMedia(variant) {
    if (!variant) return;

    const mediaId = variant.featured_media?.id || variant.featured_media_id || variant.featured_image?.id;
    if (mediaId) {
      this.switchToMediaId(mediaId);

      const activeImage = Array.from(this.images).find(
        image => String(image.dataset.mediaId) === String(mediaId)
      );
      if (activeImage) return;
    }

    const imageUrl = variant.featured_media?.preview_image?.src ||
      variant.featured_media?.preview_image?.url ||
      variant.featured_media?.src ||
      variant.featured_image?.src ||
      variant.featured_image?.url ||
      variant.featured_image_url ||
      variant.image?.src ||
      variant.image?.url;

    if (!imageUrl) return;

    const targetImage = this.images[this.currentIndex] || this.images[0];
    const image = targetImage?.querySelector('img');
    if (!targetImage || !image) return;

    this.activateImage(targetImage);
    image.removeAttribute('srcset');
    image.src = imageUrl;
    image.alt = variant.featured_image_alt || variant.featured_image?.alt || variant.title || image.alt;
  }

  activateImage(targetImage) {
    const mediaId = String(targetImage.dataset.mediaId);

    this.images.forEach(img => img.classList.remove('is-active'));
    this.thumbnails.forEach(thumb => {
      thumb.classList.toggle('is-active', String(thumb.dataset.mediaId) === mediaId);
    });

    targetImage.classList.add('is-active');
  }

  restoreMediaImage(targetImage) {
    const image = targetImage?.querySelector('img');
    if (!image || !targetImage.dataset.mediaSrc) return;

    image.src = targetImage.dataset.mediaSrc;
    image.alt = targetImage.dataset.mediaAlt || image.alt;
  }
}

class LuxuryVariantPicker {
  constructor(container) {
    this.container = container;
    this.form = container.closest('form') || document.querySelector(`#${container.dataset.section}`).querySelector('form');
    this.inputs = container.querySelectorAll('input[type="radio"]');
    this.enableHoverPreview = container.hasAttribute('data-enable-hover-preview');
    this.gallery = null;

    this.init();
  }

  init() {
    // Find gallery instance
    const galleryElement = document.querySelector('.luxury-product__gallery');
    if (galleryElement && !galleryElement.luxuryGallery) {
      galleryElement.luxuryGallery = new LuxuryProductGallery(galleryElement);
    }
    this.gallery = galleryElement?.luxuryGallery;

    // Add change listeners
    this.inputs.forEach(input => {
      input.addEventListener('change', (e) => this.handleVariantChange(e));

      // Add hover preview for color swatches
      if (this.enableHoverPreview && input.dataset.variantImage) {
        const label = input.nextElementSibling;
        if (label && label.classList.contains('luxury-variant-picker__button')) {
          label.addEventListener('mouseenter', () => {
            if (this.gallery && input.dataset.variantImage) {
              this.gallery.switchToMediaId(input.dataset.variantImage);
            }
          });
        }
      }
    });

    // Watch for variant changes from Dawn's product-info component
    this.observeVariantChanges();
  }

  handleVariantChange(event) {
    const input = event.target;

    // Update selected value display
    const optionIndex = input.dataset.optionPosition - 1;
    const selectedValueSpan = this.container.querySelector(
      `.luxury-variant-picker__selected-value[data-option-index="${optionIndex}"]`
    );
    if (selectedValueSpan) {
      selectedValueSpan.textContent = input.value;
    }

    // Switch to variant image if available
    if (this.gallery && input.dataset.variantImage) {
      this.gallery.switchToMediaId(input.dataset.variantImage);
    }

    // Update URL and product info (integrates with Dawn)
    this.updateVariant();
  }

  updateVariant() {
    // Get all selected options
    const selectedOptions = [];
    const optionGroups = {};

    this.inputs.forEach(input => {
      if (input.checked) {
        const optionName = input.name;
        if (!optionGroups[optionName]) {
          optionGroups[optionName] = input.value;
          selectedOptions.push(input.value);
        }
      }
    });

    // Find matching variant
    const variantData = this.getVariantFromOptions(selectedOptions);

    if (variantData) {
      this.updateProductInfo(variantData);
      this.updateURL(variantData.id);
      this.updatePrice(variantData);
      this.updateAvailability(variantData);
    }
  }

  getVariantFromOptions(selectedOptions) {
    const productData = JSON.parse(
      document.querySelector('[data-selected-variant]')?.textContent || '{}'
    );
    return productData;
  }

  updateProductInfo(variant) {
    // Update hidden variant ID input
    const variantInput = this.form?.querySelector('input[name="id"]');
    if (variantInput) {
      variantInput.value = variant.id;
      variantInput.disabled = !variant.available;
    }

    // Dispatch custom event for other scripts
    document.dispatchEvent(new CustomEvent('variant:change', {
      detail: { variant }
    }));
  }

  updateURL(variantId) {
    if (!window.history.replaceState) return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url.toString());
  }

  updatePrice(variant) {
    // Price updates handled by Dawn's product-info component
    // This is a placeholder for custom price formatting if needed
  }

  updateAvailability(variant) {
    const addToCartButton = document.querySelector('.luxury-buy-buttons__add-to-cart');
    if (addToCartButton) {
      const buttonText = addToCartButton.querySelector('span');
      if (variant.available) {
        addToCartButton.disabled = false;
        if (buttonText) buttonText.textContent = 'ADD TO CART';
      } else {
        addToCartButton.disabled = true;
        if (buttonText) buttonText.textContent = 'SOLD OUT';
      }
    }
  }

  observeVariantChanges() {
    // Watch for Dawn's product-info updates
    const productInfo = document.querySelector('product-info');
    if (productInfo) {
      const observer = new MutationObserver(() => {
        // Sync with any external variant changes
      });
      observer.observe(productInfo, {
        attributes: true,
        childList: true,
        subtree: true
      });
    }
  }
}

class LuxuryProductAccordion {
  constructor(container) {
    this.container = container;
    this.details = container.querySelectorAll('.accordion-item');

    this.init();
  }

  init() {
    this.details.forEach(detail => {
      const summary = detail.querySelector('.accordion-summary');

      summary.addEventListener('click', (e) => {
        e.preventDefault();
        const isOpen = detail.hasAttribute('open');

        if (isOpen) {
          this.closeAccordion(detail);
        } else {
          this.openAccordion(detail);
        }
      });
    });
  }

  openAccordion(detail) {
    detail.setAttribute('open', '');
  }

  closeAccordion(detail) {
    const content = detail.querySelector('.accordion-content');
    if (content) {
      content.addEventListener('animationend', () => {
        detail.removeAttribute('open');
      }, { once: true });
    } else {
      detail.removeAttribute('open');
    }
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initLuxuryProduct);
} else {
  initLuxuryProduct();
}

function initLuxuryProduct() {
  // Initialize Gallery
  const productInfo = document.querySelector('.luxury-product');
  const galleryElement = productInfo?.querySelector('.luxury-product__gallery');
  if (galleryElement && !galleryElement.luxuryGallery) {
    galleryElement.luxuryGallery = new LuxuryProductGallery(galleryElement);
  }

  // Dawn owns variant matching and publishes the complete variant object.
  // The custom gallery only consumes featured_media from that final result.
  if (productInfo && !productInfo.dataset.luxuryVariantBridge) {
    productInfo.dataset.luxuryVariantBridge = 'true';
    const sectionId = String(productInfo.dataset.section);
    const productId = String(productInfo.dataset.productId);
    const variantsElement = productInfo.querySelector('[data-luxury-product-variants]');
    let productVariants = [];
    try {
      productVariants = JSON.parse(variantsElement?.textContent || '[]');
    } catch (_) {}
    if (productVariants.length) {
      window.__luxuryProductVariants = window.__luxuryProductVariants || {};
      window.__luxuryProductVariants[productId] = productVariants;
    } else {
      productVariants = window.__luxuryProductVariants?.[productId] || [];
    }

    const getVariantFromSelectedOptions = () => {
      const variantSelects = productInfo.querySelector('variant-selects');
      if (!variantSelects || !productVariants.length) return null;

      const normalizeOption = (value) => String(value || '').trim();
      const selectedOptions = Array.from(
        variantSelects.querySelectorAll('fieldset input:checked, select option:checked')
      ).map((input) => normalizeOption(input.value));

      return productVariants.find((item) =>
        item.options?.length === selectedOptions.length &&
        item.options.every((option, index) => normalizeOption(option) === selectedOptions[index])
      ) || null;
    };

    const findVariantById = (variantId) => productVariants.find((item) =>
      String(item.id) === String(variantId) ||
      String(item.id).endsWith(String(variantId))
    ) || null;

    const syncVariant = (variant) => {
      if (!variant) return;
      const completeVariant = findVariantById(variant.id) || variant;
      const gallery = productInfo.querySelector('.luxury-product__gallery')?.luxuryGallery;
      gallery?.switchToVariantMedia(completeVariant);
    };

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (String(data?.sectionId) === sectionId) syncVariant(data.variant);
      });
    }

    productInfo.addEventListener('change', (event) => {
      const variantSelects = event.target.closest('variant-selects');
      if (!variantSelects) return;

      // Match against this product's own variants immediately. Dawn still handles its
      // normal section refresh; this keeps the custom gallery correct if that request fails.
      try {
        const variant = getVariantFromSelectedOptions();

        if (variant) {
          syncVariant(variant);
          productInfo.querySelectorAll('form[action*="/cart/add"] input[name="id"]').forEach((input) => {
            input.value = variant.id;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
        }
      } catch (_) {}
    });

    // Use the visible option text first; Dawn's JSON can still contain the previous
    // variant while its section request is being refreshed.
    const initialVariant = getVariantFromSelectedOptions();
    if (initialVariant) {
      syncVariant(initialVariant);
    } else {
      try {
        syncVariant(JSON.parse(productInfo.querySelector('[data-selected-variant]')?.textContent || 'null'));
      } catch (_) {}
    }
  }

  // Initialize Accordions
  const accordionContainer = document.querySelector('.luxury-product__info');
  if (accordionContainer && !accordionContainer.luxuryAccordion) {
    accordionContainer.luxuryAccordion = new LuxuryProductAccordion(accordionContainer);
  }
}

// Reinitialize on section reload (Shopify Theme Editor)
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('.luxury-product')) {
    initLuxuryProduct();
  }
});

// Handle add to cart
document.addEventListener('submit', (event) => {
  const form = event.target;

  if (form.classList.contains('luxury-product-form')) {
    event.preventDefault();

    const formData = new FormData(form);
    const button = form.querySelector('.luxury-buy-buttons__add-to-cart');
    const originalText = button?.querySelector('span')?.textContent;

    if (button) {
      button.disabled = true;
      const span = button.querySelector('span');
      if (span) span.textContent = 'ADDING...';
    }

    fetch('/cart/add.js', {
      method: 'POST',
      body: formData
    })
    .then(response => response.json())
    .then(data => {
      // Trigger cart update event
      document.dispatchEvent(new CustomEvent('cart:item-added', {
        detail: { item: data }
      }));

      // Update button
      if (button) {
        const span = button.querySelector('span');
        if (span) span.textContent = 'ADDED!';

        setTimeout(() => {
          button.disabled = false;
          if (span) span.textContent = originalText;
        }, 2000);
      }

      // Open cart drawer if available
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        cartDrawer.open();
      }
    })
    .catch(error => {
      console.error('Error adding to cart:', error);

      if (button) {
        button.disabled = false;
        const span = button.querySelector('span');
        if (span) span.textContent = originalText;
      }

      alert('Error adding item to cart. Please try again.');
    });
  }
});

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    LuxuryProductGallery,
    LuxuryVariantPicker,
    LuxuryProductAccordion
  };
}
